import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { IconType } from 'react-icons';
import {
  FiBarChart2,
  FiCalendar,
  FiChevronDown,
  FiChevronRight,
  FiEdit3,
  FiFolder,
  FiHome,
  FiSettings,
  FiUsers,
} from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import BrandLogo from './BrandLogo';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface SidebarProject {
  id: string;
  name: string;
}

type ExpandableMenu = 'projects' | 'posts' | 'analytics';

type SidebarIconComponent = React.FC<{ className?: string }>;

function asSidebarIcon(icon: IconType): SidebarIconComponent {
  return icon as unknown as SidebarIconComponent;
}

const HomeIcon = asSidebarIcon(FiHome);
const FolderIcon = asSidebarIcon(FiFolder);
const UsersIcon = asSidebarIcon(FiUsers);
const PostsIcon = asSidebarIcon(FiEdit3);
const AnalyticsIcon = asSidebarIcon(FiBarChart2);
const CalendarIcon = asSidebarIcon(FiCalendar);
const SettingsIcon = asSidebarIcon(FiSettings);
const ChevronDownIcon = asSidebarIcon(FiChevronDown);
const ChevronRightIcon = asSidebarIcon(FiChevronRight);

function resolveMenuFromPath(pathname: string): ExpandableMenu | null {
  if (pathname.startsWith('/analytics')) return 'analytics';
  if (pathname.startsWith('/posts')) return 'posts';
  if (pathname.startsWith('/projects')) return 'projects';
  return null;
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const selectedMenu = resolveMenuFromPath(location.pathname);
  const [expandedMenu, setExpandedMenu] = useState<ExpandableMenu | null>(selectedMenu);
  const [projects, setProjects] = useState<SidebarProject[]>([]);

  useEffect(() => {
    fetch(`${API_BASE}/api/projects`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setProjects(data.slice(0, 5)))
      .catch(() => {});
  }, []);

  const isActive = (path: string) => location.pathname.startsWith(path);

  const toggleMenu = (menu: ExpandableMenu) => {
    if (menu !== selectedMenu) {
      setExpandedMenu(null);
      return;
    }
    setExpandedMenu(prev => prev === menu ? null : menu);
  };

  // Keep collapsible menus aligned with current route.
  useEffect(() => {
    setExpandedMenu(selectedMenu);
  }, [selectedMenu]);

  return (
    <aside className="sidebar">
      <div className="sidebar-top">
        <div className="sidebar-logo">
          <Link to="/projects" aria-label="VimAPI — A ViralAI Platform" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
            <BrandLogo height={34} />
          </Link>
        </div>
        <nav className="sidebar-nav">
          <ul>
            <li className={isActive('/dashboard') ? 'active' : ''}>
              <Link to="/dashboard"><span className="nav-icon" aria-hidden="true"><HomeIcon /></span> Dashboard</Link>
            </li>
            <li className={`sidebar-projects-item ${isActive('/projects') ? 'active' : ''}`}>
              <div className="sidebar-projects-row">
                <Link to="/projects" onClick={() => toggleMenu('projects')}><span className="nav-icon" aria-hidden="true"><FolderIcon /></span> Projects</Link>
                <button
                  className="sidebar-expand-btn"
                  aria-label="Toggle Projects menu"
                  onClick={e => { e.preventDefault(); toggleMenu('projects'); }}
                >
                  {expandedMenu === 'projects' ? <ChevronDownIcon /> : <ChevronRightIcon />}
                </button>
              </div>
              {expandedMenu === 'projects' && (
                <ul className="sidebar-sublist">
                  {projects.map(p => (
                    <li key={p.id} className={location.pathname.includes(p.id) ? 'active' : ''}>
                      <Link to={`/projects/${p.id}`}>
                        <span className="sidebar-subitem-dot" aria-hidden="true" />
                        {p.name.length > 18 ? p.name.slice(0, 18) + '…' : p.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            <li className={isActive('/account') ? 'active' : ''}><Link to="/account"><span className="nav-icon" aria-hidden="true"><UsersIcon /></span> Social Accounts</Link></li>
            <li className={`sidebar-projects-item ${isActive('/posts') ? 'active' : ''}`}>
              <div className="sidebar-projects-row">
                <Link to="/posts" onClick={() => toggleMenu('posts')}><span className="nav-icon" aria-hidden="true"><PostsIcon /></span> Posts</Link>
                <button
                  className="sidebar-expand-btn"
                  aria-label="Toggle Posts menu"
                  onClick={e => { e.preventDefault(); toggleMenu('posts'); }}
                >
                  {expandedMenu === 'posts' ? <ChevronDownIcon /> : <ChevronRightIcon />}
                </button>
              </div>
              {expandedMenu === 'posts' && (
                <ul className="sidebar-sublist">
                  <li className={location.pathname.includes('/posts/compose') ? 'active' : ''}>
                    <Link to="/posts/compose"><span className="sidebar-subitem-dot" aria-hidden="true" />Compose</Link>
                  </li>
                  <li className={location.pathname.includes('/posts/drafts') ? 'active' : ''}>
                    <Link to="/posts/drafts"><span className="sidebar-subitem-dot" aria-hidden="true" />Drafts <span className="status-dot blue"></span></Link>
                  </li>
                  <li className={location.pathname.includes('/posts/live') ? 'active' : ''}>
                    <Link to="/posts/live"><span className="sidebar-subitem-dot" aria-hidden="true" />Live <span className="status-dot green"></span></Link>
                  </li>
                  <li className={location.pathname.includes('/posts/archived') ? 'active' : ''}>
                    <Link to="/posts/archived"><span className="sidebar-subitem-dot" aria-hidden="true" />Archived</Link>
                  </li>
                  <li className={location.pathname.includes('/posts/scheduled') ? 'active' : ''}>
                    <Link to="/posts/scheduled"><span className="sidebar-subitem-dot" aria-hidden="true" />Scheduled</Link>
                  </li>
                </ul>
              )}
            </li>
            <li className={`sidebar-projects-item ${isActive('/analytics') ? 'active' : ''}`}>
              <div className="sidebar-projects-row">
                <Link to="/analytics" onClick={() => toggleMenu('analytics')}><span className="nav-icon" aria-hidden="true"><AnalyticsIcon /></span> Analytics</Link>
                <button
                  className="sidebar-expand-btn"
                  aria-label="Toggle Analytics menu"
                  onClick={e => { e.preventDefault(); toggleMenu('analytics'); }}
                >
                  {expandedMenu === 'analytics' ? <ChevronDownIcon /> : <ChevronRightIcon />}
                </button>
              </div>
              {expandedMenu === 'analytics' && (
                <ul className="sidebar-sublist">
                  <li className={location.pathname.includes('/analytics/insights') ? 'active' : ''}>
                    <Link to="/analytics/insights"><span className="sidebar-subitem-dot" aria-hidden="true" />Insights <span className="status-dot green"></span></Link>
                  </li>
                </ul>
              )}
            </li>
            <li>
              <span className="sidebar-static-item"><span className="nav-icon" aria-hidden="true"><CalendarIcon /></span>Schedule</span>
            </li>
            <li>
              <span className="sidebar-static-item"><span className="nav-icon" aria-hidden="true"><SettingsIcon /></span>Settings</span>
            </li>
          </ul>
        </nav>
      </div>
      <div className="sidebar-bottom">
        <div className="storage-indicator">
          <div className="storage-label">
            <span>Storage</span>
            <span>65%</span>
          </div>
          <div className="storage-track">
            <div className="storage-bar" />
          </div>
        </div>
        <button className="theme-toggle" onClick={toggleTheme}>
          <span className="theme-toggle-track">
            <span className="theme-toggle-thumb" />
          </span>
          <span className="theme-toggle-label">{theme === 'dark' ? 'Dark' : 'Light'}</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
